import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { expandClinicalSignificanceFilter } from '../utils/clinicalSignificanceMapping';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';

const API_BASE_URL = process.env.REACT_APP_ONCOVIZ_UI_V2_API_BASE_URL;

export const useDateRange = (filters = {}) => {
  const [dateRange, setDateRange] = useState({ minDate: null, maxDate: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasApiData, setHasApiData] = useState(false);

  // Stringify filters to use as stable dependency
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    const fetchDateRange = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {};

        // Add genes parameter if available
        if (filters.genes && Array.isArray(filters.genes) && filters.genes.length > 0) {
          params.genes = filters.genes.map((gene) => gene.biomarkerName);
        }

        // Add testNameDetails (NGS/test type) parameter if available
        if (filters.testNameDetails && Array.isArray(filters.testNameDetails) && filters.testNameDetails.length > 0) {
          params.testNameDetails = filters.testNameDetails.map((test) => test.name);
        }

        // Add cancerSite filter if available
        if (filters.cancerSite && Array.isArray(filters.cancerSite) && filters.cancerSite.length > 0) {
          params.cancerSite = filters.cancerSite;
        }

        // Add other filters
        if (filters.immunoTherapy && Array.isArray(filters.immunoTherapy) && filters.immunoTherapy.length > 0) {
          params.immunoTherapy = filters.immunoTherapy.map((immuno) => immuno.name);
        }

        if (filters.targetedTherapy && Array.isArray(filters.targetedTherapy) && filters.targetedTherapy.length > 0) {
          params.targetedTherapy = filters.targetedTherapy.map((targeted) => targeted.name);
        }

        if (filters.morphologyTypes && Array.isArray(filters.morphologyTypes) && filters.morphologyTypes.length > 0) {
          params.morphologyTypes = filters.morphologyTypes.map((morph) => morph.name);
        }

        if (filters.hospitalCity && Array.isArray(filters.hospitalCity) && filters.hospitalCity.length > 0) {
          params.hospitalCity = filters.hospitalCity.map((city) => city.name);
        }

        if (filters.hospitalName && Array.isArray(filters.hospitalName) && filters.hospitalName.length > 0) {
          params.hospitalName = filters.hospitalName.map((hospital) => hospital.name);
        }

        if (filters.countryFilter && Array.isArray(filters.countryFilter) && filters.countryFilter.length > 0) {
          params.countryFilter = filters.countryFilter.map((item) => item.name);
        }

        if (filters.stateFilter && Array.isArray(filters.stateFilter) && filters.stateFilter.length > 0) {
          params.stateFilter = filters.stateFilter.map((item) => item.name);
        }
        
        if (filters.genderFilter && Array.isArray(filters.genderFilter) && filters.genderFilter.length > 0) {
          params.genderFilter = filters.genderFilter;
        }

        if (filters.doctorsData && Array.isArray(filters.doctorsData) && filters.doctorsData.length > 0) {
          params.doctorsData = filters.doctorsData.map((doctor) => doctor.name);
        }

        if (filters.clinicalSignificanceOfVariance && Array.isArray(filters.clinicalSignificanceOfVariance) && filters.clinicalSignificanceOfVariance.length > 0) {
          params.clinicalSignificanceOfVariance = expandClinicalSignificanceFilter(filters.clinicalSignificanceOfVariance);
        }

        // Add configuration flags
        if (filters.ngsFilterType) {
          params.ngsFilterType = filters.ngsFilterType;
        }

        if (filters.pdlFilter !== undefined) {
          params.pdlFilter = filters.pdlFilter;
        }

        if (filters.lungsAlliance !== undefined) {
          params.lungsAlliance = filters.lungsAlliance;
        }

        if (filters.is1kViewEnabled !== undefined) {
          params.is1kViewEnabled = filters.is1kViewEnabled;
        }

        if (filters.ngsGeneDetectFilter) {
          params.ngsGeneDetectFilter = filters.ngsGeneDetectFilter;
        }

        const authToken = LocalStorageService.get(LocalStorageKeys.USER_TOKEN);

        const response = await axios.get(`${API_BASE_URL}/ov2/min_max_enrolled_on`, {
          params,
          paramsSerializer: (params) => {
            const searchParams = new URLSearchParams();

            Object.keys(params).forEach((key) => {
              const value = params[key];

              if (Array.isArray(value)) {
                // Handle Arrays - append each value separately
                value.forEach((val) => {
                  searchParams.append(key, val);
                });
              } else if (value !== undefined && value !== null) {
                // Handle regular strings/numbers/booleans
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          },
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        const { success, payLoad } = response.data || {};
        if (success && payLoad) {
          const { min_date, max_date } = payLoad;

          if (min_date || max_date) {
            setDateRange({
              minDate: min_date ? new Date(min_date) : null,
              maxDate: max_date ? new Date(max_date) : new Date(),
            });
            setHasApiData(true);
          }
        } else {
          // No data from API
          setDateRange({ minDate: null, maxDate: null });
          setHasApiData(false);
        }
      } catch (err) {
        console.warn('Date range API not available or failed:', err.message);
        setError(err);
        // API not available - disable calendars
        setDateRange({ minDate: null, maxDate: null });
        setHasApiData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchDateRange();
  }, [filtersKey]);

  return { dateRange, loading, error, hasApiData };
};    